import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  FileSearch,
  Loader2,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Users,
  Calendar,
  BookOpen,
  Shield,
  ListChecks,
  Info,
} from 'lucide-react';
import { analyzeDocument, DocumentAnalysis } from '../services/claudeAI';

interface AIDocumentAnalyzerProps {
  isOpen: boolean;
  onClose: () => void;
  documentContent: string;
  documentType: string;
}

export function AIDocumentAnalyzer({
  isOpen,
  onClose,
  documentContent,
  documentType,
}: AIDocumentAnalyzerProps) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<DocumentAnalysis | null>(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'summary' | 'parties' | 'terms' | 'risks'>('summary');

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    setError('');

    try {
      const result = await analyzeDocument(documentContent, documentType);
      setAnalysis(result);
    } catch (err) {
      console.error('Analysis error:', err);
      setError('Failed to analyze document. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleClose = () => {
    setAnalysis(null);
    setError('');
    setActiveTab('summary');
    onClose();
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case 'medium':
        return <AlertCircle className="w-4 h-4 text-amber-400" />;
      default:
        return <Info className="w-4 h-4 text-blue-400" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-red-500/10 border-red-500/20';
      case 'medium':
        return 'bg-amber-500/10 border-amber-500/20';
      default:
        return 'bg-blue-500/10 border-blue-500/20';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-3xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <FileSearch className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">AI Document Analysis</h2>
                  <p className="text-sm text-slate-400">
                    Analyze document for risks, obligations, and key terms
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {!analysis && !isAnalyzing && (
                <div className="text-center py-8">
                  <FileSearch className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    Ready to Analyze
                  </h3>
                  <p className="text-slate-400 mb-6 max-w-md mx-auto">
                    AI will review your {documentType} and provide insights on parties,
                    obligations, key terms, and potential risks.
                  </p>
                  
                  {error && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    onClick={handleAnalyze}
                    className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-xl font-medium transition-colors"
                  >
                    Analyze Document
                  </button>
                </div>
              )}

              {isAnalyzing && (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 text-cyan-500 animate-spin mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    Analyzing Document
                  </h3>
                  <p className="text-slate-400">
                    AI is reviewing the document for key information...
                  </p>
                </div>
              )}

              {analysis && (
                <div className="space-y-6">
                  {/* Tabs */}
                  <div className="flex gap-2 border-b border-slate-700 pb-4">
                    {[
                      { id: 'summary', label: 'Summary', icon: BookOpen },
                      { id: 'parties', label: 'Parties', icon: Users },
                      { id: 'terms', label: 'Key Terms', icon: ListChecks },
                      { id: 'risks', label: 'Risks', icon: Shield },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          activeTab === tab.id
                            ? 'bg-cyan-600 text-white'
                            : 'text-slate-400 hover:text-white hover:bg-slate-800'
                        }`}
                      >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Summary Tab */}
                  {activeTab === 'summary' && (
                    <div className="space-y-4">
                      <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                        <h4 className="font-medium text-white mb-2">Document Summary</h4>
                        <p className="text-slate-300">{analysis.summary}</p>
                      </div>

                      {/* Dates */}
                      {analysis.dates.length > 0 && (
                        <div>
                          <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-cyan-400" />
                            Important Dates
                          </h4>
                          <div className="space-y-2">
                            {analysis.dates.map((date, i) => (
                              <div
                                key={i}
                                className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                              >
                                <span className="text-slate-300">{date.description}</span>
                                <span className="text-white font-medium">{date.date}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Missing Elements */}
                      {analysis.missingElements.length > 0 && (
                        <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                          <h4 className="font-medium text-amber-400 mb-2 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            Potentially Missing Elements
                          </h4>
                          <ul className="space-y-1 text-sm text-amber-300/80">
                            {analysis.missingElements.map((element, i) => (
                              <li key={i}>• {element}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Parties Tab */}
                  {activeTab === 'parties' && (
                    <div className="space-y-4">
                      <div className="grid gap-3">
                        {analysis.parties.map((party, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                          >
                            <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                              <Users className="w-5 h-5 text-slate-400" />
                            </div>
                            <div>
                              <p className="text-xs text-slate-400 uppercase tracking-wide">
                                {party.role}
                              </p>
                              <p className="text-white font-medium">{party.name}</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Obligations */}
                      {analysis.obligations.length > 0 && (
                        <div>
                          <h4 className="font-medium text-white mb-3">Obligations</h4>
                          <div className="space-y-2">
                            {analysis.obligations.map((obligation, i) => (
                              <div
                                key={i}
                                className="p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                              >
                                <span className="text-cyan-400 font-medium">
                                  {obligation.party}:
                                </span>{' '}
                                <span className="text-slate-300">{obligation.obligation}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Key Terms Tab */}
                  {activeTab === 'terms' && (
                    <div className="space-y-3">
                      {analysis.keyTerms.map((term, i) => (
                        <div
                          key={i}
                          className="p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                        >
                          <h4 className="font-medium text-white mb-1">{term.term}</h4>
                          <p className="text-sm text-slate-400">{term.definition}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Risks Tab */}
                  {activeTab === 'risks' && (
                    <div className="space-y-3">
                      {analysis.risks.length === 0 ? (
                        <div className="text-center py-8">
                          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                          <p className="text-white font-medium">No significant risks identified</p>
                          <p className="text-sm text-slate-400">
                            The document appears to be well-structured
                          </p>
                        </div>
                      ) : (
                        analysis.risks.map((risk, i) => (
                          <div
                            key={i}
                            className={`p-4 rounded-lg border ${getSeverityColor(risk.severity)}`}
                          >
                            <div className="flex items-start gap-3">
                              {getSeverityIcon(risk.severity)}
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-medium text-white">{risk.risk}</h4>
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full ${
                                      risk.severity === 'high'
                                        ? 'bg-red-500/20 text-red-400'
                                        : risk.severity === 'medium'
                                        ? 'bg-amber-500/20 text-amber-400'
                                        : 'bg-blue-500/20 text-blue-400'
                                    }`}
                                  >
                                    {risk.severity}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-400">
                                  <span className="font-medium">Recommendation:</span>{' '}
                                  {risk.recommendation}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {analysis && (
              <div className="flex items-center justify-end p-6 border-t border-slate-700">
                <button
                  onClick={handleClose}
                  className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default AIDocumentAnalyzer;
